import axios from 'axios'
import AppConstants from '../constants/app.constants'
import HTTPUtils from '../_helpers/http.util'
import RouterUtils from '../_helpers/router.util'
import ErrorMessageUtils from '../_helpers/error-message.util'
import FlashMessage from './flash-message.component'
import { store } from '../store'
import AuthUtils from '../_helpers/auth.util'

export default class HTTPClient {
  constructor(config = null, onSuccess = null, onError = null) {
    this.onSuccess = onSuccess
    this.onError = onError
    // Axios instances used to send requests to WES API
    this.httpBlob = this.initAxiosInstance(AppConstants.API_URL, true, true, false, false)
    this.http = this.initAxiosInstance(AppConstants.API_URL, true, false, false, false)
    this.httpLogin = this.initAxiosInstance(config.USERS_BASE_URL, false, false, false, false)
    this.httpMock = this.initAxiosInstance(AppConstants.MOCK_API_URL, true, false, false, false)
    // Axios instances used to send requests to Media & User API
    this.httpMedia = this.initAxiosInstance(config.TOP_MEDIA_URL, false, false, true, true)
    this.httpUsers = this.initAxiosInstance(config.USERS_BASE_URL, true, false, true, true)
    this.httpUsersNoToken = this.initAxiosInstance(config.USERS_BASE_URL, false, false, true, true)
    // Axios instances used to send requests to Plus Hub API
    this.httpPlusHub = this.initAxiosInstance(config.PLUS_HUB_BASE_URL, true, false, true, true)
    this.httpStatic = this.initAxiosInstance('/', false, false, false, false)
  }

  initAxiosInstance(baseURL, initToken, isBlobResponse, handleCommonResponse, handleCommonError) {
    let instance = axios
    // eslint-disable-next-line no-undef
    let axiosDefaultTimeout = config.AXIOS_DEFAULT_TIMEOUT
    // eslint-disable-next-line no-undef
    let axiosLongResponseTimeout = config.AXIOS_LONG_RESPONSE_TIMEOUT
    // eslint-disable-next-line no-undef
    let axiosMediumResponseTimeout = config.AXIOS_MEDIUM_RESPONSE_TIMEOUT
    if (baseURL) {
      instance = this.createAxiosInstance(baseURL, isBlobResponse)
    }

    instance.interceptors.request.use(function(config) {
      instance.defaults.timeout = axiosDefaultTimeout
      if (config.url) {
        let isLongResponseURL = config.url.match(AppConstants.LONG_RESPONSE_URL_REGEX)
        if (!isLongResponseURL && config.method === 'post') {
          isLongResponseURL = config.url.match(/\/upload/)
        }
        let isMediumResponseURL = config.url.match(AppConstants.MEDIUM_RESPONSE_URL_REGEX)
        if (!isMediumResponseURL && config.method === 'delete') {
          isMediumResponseURL = true
        }
        if (isLongResponseURL) {
          instance.defaults.timeout = axiosLongResponseTimeout
          config.timeout = axiosLongResponseTimeout
        } else if (isMediumResponseURL) {
          instance.defaults.timeout = axiosMediumResponseTimeout
          config.timeout = axiosMediumResponseTimeout
        }
      }
      // Check to add Bearer Token to request header
      if (initToken) {
        const token =
          sessionStorage.getItem(AppConstants.TOKEN) ||
          localStorage.getItem(AppConstants.TOKEN)
        if (token) {
          config.headers.Authorization = 'Bearer ' + token
          const loginMode =
            sessionStorage.getItem(AppConstants.STORAGE_LOGIN_MODE) ||
            localStorage.getItem(AppConstants.STORAGE_LOGIN_MODE)
          config.headers['X-Login-Mode'] = loginMode
          if (loginMode === AppConstants.ENTRA_LOGIN_MODE) {
            const loginTimestamp = localStorage.getItem(AppConstants.STORAGE_LOGIN_TIMESTAMP) || ''
            if (loginTimestamp) {
              config.headers['X-Login-Timestamp'] = loginTimestamp
            }
          }
        }
      }
      // Check to encode all params in GET method
      if (config.method === 'get' && config.params) {
        let url = config.url + '?'
        for (const propName of Object.keys(config.params)) {
          const value = config.params[propName]
          var part = RouterUtils.encodedUrlParam(propName) + '='
          if (value !== null && typeof value !== 'undefined') {
            if (typeof value === 'object') {
              for (const key of Object.keys(value)) {
                let params = propName + '[' + key + ']'
                var subPart = RouterUtils.encodedUrlParam(params) + '='
                url += subPart + RouterUtils.encodedUrlParam(value[key]) + '&'
              }
            } else {
              url += part + RouterUtils.encodedUrlParam(value) + '&'
            }
          }
        }
        url = url.slice(0, -1)
        config.params = {}
        config.url = url
      }
      return config
    })

    // eslint-disable-next-line no-undef
    const authMode = config.AUTH_MODE

    instance.interceptors.response.use(
      async response => {
        if (navigator.onLine) {
          await FlashMessage.getInstance().hideDisconnectionErrorMessage()
        }

        // Handle 401 error
        if (authMode && initToken && HTTPUtils.is401Error(response)) {
          AuthUtils.handleExpiredToken(response && response.data && response.data.errMsg)
          throw new Error('401')
        }

        // Handle 403 error
        if (initToken && HTTPUtils.isForbiddenResponse(response)) {
          RouterUtils.goToRouterPath('/')
          throw new Error('403')
        }

        // Check to display common response error
        if (handleCommonResponse && HTTPUtils.isInvalidResponse(response)) {
          const errorMsg = response.msg ? response.msg : (response.data ? (response.data.msg ? response.data.msg : '無効な応答') : '無効な応答')
          if (this.onError) {
            this.onError(errorMsg)
          }
          throw new Error(errorMsg)
        }

        // Return normal response
        return response
      },
      async error => {
        if (AppConstants.HEALTH_CHECK_URL === baseURL) {
          throw error
        }

        if (navigator.onLine && !store.getters['socket/isSocketEnabled']) {
          await FlashMessage.getInstance().hideDisconnectionErrorMessage()
        }

        const showDisconnectionErrorMsgFlag = await ErrorMessageUtils.checkToShowDisconnectionErrorMsg(error, true)
        if (showDisconnectionErrorMsgFlag) {
          throw error
        }

        if (authMode && initToken && HTTPUtils.isExpiredTokenError(error)) {
          const { response } = error
          AuthUtils.handleExpiredToken(response && response.data && response.data.errMsg)
          throw error
        }

        if (handleCommonError) {
          console.error(error)
          if (this.onError) {
            this.onError(error)
          }
        }
        // just throw the current error (to maintain old logic)
        throw error
      }
    )

    return instance
  }

  createAxiosInstance(baseURL, isBlobResponse) {
    if (isBlobResponse) {
      return axios.create({
        baseURL,
        responseType: 'blob'
      })
    }
    return axios.create({
      baseURL
    })
  }

  static getInstance(config = null, onSuccess = null, onError = null) {
    if (!this.instance) {
      this.instance = new HTTPClient(config, onSuccess, onError)
    }
    return this.instance
  }
}



// WEBPACK FOOTER //
// ./src/common/http-client.js